import * as React from 'react'
import moment from 'moment'

import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import { DateAndTimeField } from '@/ui/form/controls/DateAndTimeField'
import { FaIcon, faClose } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useDriverService from '@/modules/drivers/services/DriverService'
import useVehicleActivityService from '@/modules/vehicles/services/ActivityService'
import { DriverCalendarDayModel } from '@/app/models/calendar/driverCalendarDayModel'

type Props = {
  companyId: string
  vehicleId: number
  day: DriverCalendarDayModel
  selectedDays?: Array<DriverCalendarDayModel>
  close: () => void
  onSuccess?: () => Promise<void> | void
}

const REQUEST_DATE_FORMAT = 'YYYY-MM-DD HH:mm'

const toRequestDate = (value: string): string => {
  const parsed = moment(value, [REQUEST_DATE_FORMAT, moment.ISO_8601], true)
  return (parsed.isValid() ? parsed : moment(value)).format(REQUEST_DATE_FORMAT)
}

const AssignActivitiesToDriverModal: React.FC<Props> = ({
  companyId,
  vehicleId,
  day,
  selectedDays = [],
  close,
  onSuccess,
}) => {
  const { t } = useLocalize()
  const { getDrivers } = useDriverService()
  const { assignActivitiesToDriver } = useVehicleActivityService()

  const [drivers, setDrivers] = React.useState<any[] | null>(null)
  const [selectedDriverId, setSelectedDriverId] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(true)
  const [errorMessage, setErrorMessage] = React.useState('')

  // Default the period from the selected day(s): first day at 00:00 up to the
  // last selected day at 23:59 (WPF: 23:59:59).
  const { defaultFrom, defaultTo } = React.useMemo(() => {
    const days = selectedDays.length > 0 ? selectedDays : [day]
    const dates = days
      .map((d) => moment(d.activityDayLocalTimeDate))
      .filter((m) => m.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf())
    const first = dates[0] ?? moment()
    const last = dates[dates.length - 1] ?? moment()
    return {
      defaultFrom: first.clone().startOf('day').format(REQUEST_DATE_FORMAT),
      defaultTo: last.clone().endOf('day').format(REQUEST_DATE_FORMAT),
    }
  }, [selectedDays, day])

  const [dateFrom, setDateFrom] = React.useState(defaultFrom)
  const [dateTo, setDateTo] = React.useState(defaultTo)

  React.useEffect(() => {
    const fetchDrivers = async () => {
      setIsLoading(true)
      try {
        const data = await getDrivers({ companyId, onlyActive: true })
        setDrivers(data)
        if (data?.length > 0) {
          setSelectedDriverId(String(data[0].id))
        }
      } catch {
        setErrorMessage(t('common.error'))
      } finally {
        setIsLoading(false)
      }
    }
    fetchDrivers()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    if (!selectedDriverId || !dateFrom || !dateTo) {
      setErrorMessage(t('driverModule.all-fields-required'))
      return
    }

    const from = toRequestDate(dateFrom)
    const to = toRequestDate(dateTo)

    if (!moment(from, REQUEST_DATE_FORMAT).isBefore(moment(to, REQUEST_DATE_FORMAT))) {
      setErrorMessage(t('assignActivitiesToDriverPopup.date-range-invalid'))
      return
    }

    setIsLoading(true)
    try {
      await assignActivitiesToDriver({
        companyId,
        selectedDriverId: Number(selectedDriverId),
        selectedVehicleId: String(vehicleId),
        selectedDates: null,
        dateFrom: from,
        dateTo: to,
        year: moment(from).year(),
      })
      if (onSuccess) await onSuccess()
      close()
    } catch (error) {
      console.error('Error assigning activities to driver:', error)
      setErrorMessage(t('common.error'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ModalLayout>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="tw-relative tw-z-[10000] tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[450px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading && (
          <div className="tw-absolute tw-inset-0 tw-z-[10001] tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-black tw-bg-opacity-50">
            <div className="tw-h-12 tw-w-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-gray-200 tw-border-t-primary"></div>
          </div>
        )}

        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-px-6 tw-py-4">
          <p className="tw-text-lg tw-font-semibold tw-text-content-base">
            {t('assignActivitiesToDriverPopup.title')}
          </p>
          <FaIcon
            icon={faClose}
            className="tw-h-5 tw-w-5 tw-cursor-pointer"
            onClick={close}
          />
        </div>

        <div className="tw-space-y-4 tw-p-6">
          <FieldsetSelect
            required
            label={t('assignActivitiesToDriverPopup.driver')}
            name="driver"
            value={selectedDriverId}
            disabled={!drivers || drivers.length === 0}
            placeholder={
              !drivers || drivers.length === 0
                ? t('common.loading')
                : t('driverModule.select-driver')
            }
            onChange={(e) => {
              setErrorMessage('')
              setSelectedDriverId(e.target.value)
            }}
            options={
              !drivers || drivers.length === 0
                ? [{ label: t('common.loading'), value: '' }]
                : drivers.map((driver) => ({
                    label: `${driver.fullName}`,
                    value: String(driver.id),
                  }))
            }
          />

          <DateAndTimeField
            required
            label={t('assignActivitiesToDriverPopup.dateFrom')}
            value={dateFrom}
            setValue={(v) => {
              setErrorMessage('')
              setDateFrom(v ?? '')
            }}
          />

          <DateAndTimeField
            required
            label={t('assignActivitiesToDriverPopup.dateTo')}
            value={dateTo}
            setValue={(v) => {
              setErrorMessage('')
              setDateTo(v ?? '')
            }}
          />

          {errorMessage && (
            <p className="tw-mt-1 tw-text-sm tw-text-red-500">{errorMessage}</p>
          )}
        </div>

        <div className="tw-flex tw-justify-end tw-gap-3 tw-border-t tw-border-base-300 tw-px-6 tw-py-4">
          <button
            type="button"
            onClick={close}
            className="tw-rounded-lg tw-border tw-border-base-300 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-200"
          >
            {t('answer.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
          >
            {t('assignActivitiesToDriverPopup.assign')}
          </button>
        </div>
      </form>
    </ModalLayout>
  )
}

export default AssignActivitiesToDriverModal
